import { useCallback, useEffect, useRef, useState } from 'react';
import { PWA_KEYS, readInstallFlag, writeInstallFlag, isStandalone, installPlatform } from '../lib/pwa';

export function usePwaInstall() {
  const [installed, setInstalled] = useState(() => isStandalone() || readInstallFlag(PWA_KEYS.installed));
  const installedRef = useRef(installed);
  const [suggestion, setSuggestion] = useState(false), [instructions, setInstructions] = useState(false);
  const [earlySuggestion, setEarlySuggestion] = useState(() => installPlatform().name === 'ios' &&
    !isStandalone() && !Object.values(PWA_KEYS).some(readInstallFlag));
  const [nativeAvailable, setNativeAvailable] = useState(false), [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const nativeEvent = useRef(null), prompting = useRef(false);
  const suppressed = useRef(readInstallFlag(PWA_KEYS.offered) || readInstallFlag(PWA_KEYS.dismissed));
  const firstSave = useRef(readInstallFlag(PWA_KEYS.firstSave));
  const platform = installPlatform();

  const markInstalled = useCallback(() => {
    const newlyInstalled = !installedRef.current; installedRef.current = true;
    writeInstallFlag(PWA_KEYS.installed); writeInstallFlag(PWA_KEYS.offered);
    suppressed.current = true; nativeEvent.current = null;
    setInstalled(true); setNativeAvailable(false); setSuggestion(false); setEarlySuggestion(false);
    if (newlyInstalled) setInstructions(false);
  }, []);
  const dismiss = useCallback(() => {
    writeInstallFlag(PWA_KEYS.dismissed); writeInstallFlag(PWA_KEYS.offered);
    suppressed.current = true; setSuggestion(false); setEarlySuggestion(false); setInstructions(false); setError('');
  }, []);

  useEffect(() => {
    const modes = ['(display-mode: standalone)', '(display-mode: window-controls-overlay)'].map(query => window.matchMedia(query));
    const checkInstalled = () => {
      if (isStandalone()) { markInstalled(); setInstructions(false); }
      else if (readInstallFlag(PWA_KEYS.installed)) markInstalled();
    };
    const confirmedInstalled = () => { markInstalled(); setInstructions(false); };
    const beforeInstall = event => {
      // Capture the browser event without showing any first-visit install wall.
      event.preventDefault();
      if (isStandalone() || readInstallFlag(PWA_KEYS.installed)) return;
      nativeEvent.current = event; setNativeAvailable(true);
    };
    const storage = event => {
      if (event.key === PWA_KEYS.installed) checkInstalled();
      if ([PWA_KEYS.offered, PWA_KEYS.dismissed].includes(event.key) && event.newValue === '1') {
        suppressed.current = true; setSuggestion(false); setEarlySuggestion(false);
      }
      if (event.key === PWA_KEYS.firstSave && event.newValue === '1') { firstSave.current = true; setEarlySuggestion(false); }
    };
    const visible = () => { if (document.visibilityState === 'visible') checkInstalled(); };
    checkInstalled();
    window.addEventListener('beforeinstallprompt', beforeInstall);
    window.addEventListener('appinstalled', confirmedInstalled);
    window.addEventListener('storage', storage);
    document.addEventListener('visibilitychange', visible);
    modes.forEach(mode => mode.addEventListener?.('change', checkInstalled));
    return () => {
      window.removeEventListener('beforeinstallprompt', beforeInstall);
      window.removeEventListener('appinstalled', confirmedInstalled);
      window.removeEventListener('storage', storage);
      document.removeEventListener('visibilitychange', visible);
      modes.forEach(mode => mode.removeEventListener?.('change', checkInstalled));
    };
  }, [markInstalled]);

  const onSuccessfulSave = useCallback(() => {
    if (firstSave.current || readInstallFlag(PWA_KEYS.firstSave)) return;
    firstSave.current = true; writeInstallFlag(PWA_KEYS.firstSave);
    setEarlySuggestion(false);
    // iOS receives the optional pre-save message instead of a second offer
    // immediately after saving into the browser's separate guest session.
    if (installPlatform().name === 'ios') {
      suppressed.current = true; writeInstallFlag(PWA_KEYS.offered); return;
    }
    if (suppressed.current || readInstallFlag(PWA_KEYS.offered) || readInstallFlag(PWA_KEYS.dismissed)) return;
    if (isStandalone() || readInstallFlag(PWA_KEYS.installed)) { markInstalled(); return; }
    // Persist the offer immediately: reloading or reopening must not repeat it.
    suppressed.current = true; writeInstallFlag(PWA_KEYS.offered);
    setSuggestion(true);
  }, [markInstalled]);

  const install = useCallback(async () => {
    if (prompting.current) return;
    if (isStandalone()) { markInstalled(); return; }
    setEarlySuggestion(false);
    setError('');
    if (installPlatform().inAppBrowser) { setSuggestion(false); setInstructions(true); return; }
    const event = nativeEvent.current;
    if (!event) { setSuggestion(false); setInstructions(true); return; }
    prompting.current = true; nativeEvent.current = null; setNativeAvailable(false); setBusy(true);
    suppressed.current = true; writeInstallFlag(PWA_KEYS.offered); setSuggestion(false); setInstructions(false);
    try {
      // Called synchronously from the Install button's click, before any await:
      // Chromium requires a user gesture, not an automatic post-save prompt().
      const shown = event.prompt();
      await shown;
      const choice = await event.userChoice;
      if (choice?.outcome === 'dismissed') dismiss();
      // Acceptance suppresses future suggestions. Only appinstalled/display
      // mode confirms installation; don't mislabel a failed install as done.
    } catch {
      if (!isStandalone() && !readInstallFlag(PWA_KEYS.installed)) {
        setError('The install window couldn’t open. You can use your browser’s menu instead.');
        setInstructions(true);
      }
    } finally { prompting.current = false; setBusy(false); }
  }, [dismiss, markInstalled]);

  return { installed, suggestion, earlySuggestion, instructions, nativeAvailable, busy, error, platform,
    install, dismiss, onSuccessfulSave };
}